import {createContext, FC, useContext, useEffect, useMemo} from "react";
import {CommonEntityProvider, ICommonComponentProps} from "../../definitions/common.definitions";
import {useGet} from "../../transport/useGet";
import {EndpointsEnum} from "../../constants/endpoints";
import type {components} from "../../shared/api/v1/openapi.types";
import {useMessage} from "../../providers/message/message-provider";

type PartnerEntityT = components['schemas']['Partner'];
type ConnectionEntityT = components['schemas']['Connection'];
type EventEntityT = components['schemas']['Event'];
type EventsResponseT = {
  events?: EventEntityT[];
  total?: number;
};

export type CabinetContextT = CommonEntityProvider<PartnerEntityT[]> & {
  partners: PartnerEntityT[];
  connections: ConnectionEntityT[];
  connectionMap: Record<number, ConnectionEntityT>;
  connectionsLoading: boolean;
  eventsTotal: number;
  refetch: () => void;
}

const context = createContext<CabinetContextT>(null!);
const CabinetContextProvider = context.Provider;
const useCabinet = () => useContext(context);

const CabinetProvider: FC<ICommonComponentProps> = (props) => {
  const {
    children,
  } = props;
  const {
    showError,
  } = useMessage();

  const {
    isLoading,
    data,
    error,
    mutate,
  } = useGet<PartnerEntityT[]>({
    key: EndpointsEnum.PARTNERS,
    auth: true,
  });
  const {
    isLoading: connectionsLoading,
    data: connectionsData,
    error: connectionsError,
  } = useGet<ConnectionEntityT[]>({
    key: EndpointsEnum.CONNECTIONS,
    auth: true,
  });
  const hasPartner = (data?.length ?? 0) > 0;
  const {
    data: eventsData,
    error: eventsError,
  } = useGet<EventsResponseT>({
    key: hasPartner ? EndpointsEnum.EVENTS_MY : null,
    auth: true,
    params: {
      page: 1,
      limit: 1,
    },
  });

  const partners = data ?? [];
  const connections = useMemo(() => connectionsData ?? [], [connectionsData]);
  const eventsTotal = eventsData?.total ?? eventsData?.events?.length ?? 0;
  const connectionMap = useMemo(
    () => connections.reduce<Record<number, ConnectionEntityT>>((acc, curr) => {
      if (typeof curr.id === 'number') {
        acc[curr.id] = curr;
      }
      return acc;
    }, {}),
    [connections],
  );
  const refetch = () => {
    mutate();
  }

  useEffect(() => {
    if (!error) {
      return;
    }
    const message = (error as any)?.response?.data?.message ?? (error as any)?.message ?? 'Ошибка загрузки партнеров';
    showError({
      message,
    });
  }, [error, showError]);
  useEffect(() => {
    if (!connectionsError) {
      return;
    }
    const message = (connectionsError as any)?.response?.data?.message ?? (connectionsError as any)?.message ?? 'Ошибка загрузки заявок';
    showError({
      message,
    });
  }, [connectionsError, showError]);
  useEffect(() => {
    if (!eventsError) {
      return;
    }
    const message = (eventsError as any)?.response?.data?.message ?? (eventsError as any)?.message ?? 'Ошибка загрузки событий';
    showError({
      message,
    });
  }, [eventsError, showError]);

  const ctx: CabinetContextT = {
    data,
    partners,
    isLoading,
    connections,
    connectionMap,
    connectionsLoading,
    eventsTotal,
    refetch,
  }

  return (
    <CabinetContextProvider
      value={ctx}
    >
      {children}
    </CabinetContextProvider>
  )
}

export default CabinetProvider;
export {
  CabinetProvider,
  useCabinet,
}
